import type { Cell, Column, Header, Table } from '@tanstack/react-table';
import type { CSSProperties, HTMLAttributes } from 'react';

function findSameLevelHeaders<T>(
	header: Header<T, unknown>,
	headers: Header<T, unknown>[]
) {
	return headers.filter((flatHeaderItem) => {
		return flatHeaderItem.depth === header.depth;
	});
}

function columnIsLastLeftSticky<T>(
	header: Header<T, unknown>,
	headers: Header<T, unknown>[]
): boolean {
	const index = headers.findIndex((h) => h.column.id === header.column.id);
	const column = headers.at(index)?.column;
	const nextColumn = headers.at(index + 1)?.column;

	return (column?.getIsPinned() ?? false) && !nextColumn?.getIsPinned();
}

/**
 * Depends on `calculateColumnWidths` from flex-layout having run first
 * (which sets the left position).
 */
function getStickySharedProps<T>(
	column: Column<T>
): HTMLAttributes<HTMLDivElement> {
	const style: CSSProperties = {};
	const dataAttributes: HTMLAttributes<HTMLDivElement> = {};

	if (column.getIsPinned() || column.parent?.getIsPinned()) {
		style.position = 'sticky';
		style.left = column.getStart('left');
		style.zIndex = 2;

		// @ts-expect-error `data-*` attributes are not in the type definition
		dataAttributes['data-sticky-cell'] = true;
	}

	return { style, ...dataAttributes };
}

export function getStickyHeaderProps<T>(
	header: Header<T, unknown>
): HTMLAttributes<HTMLDivElement> {
	return getStickySharedProps(header.column);
}

export function getStickyCellProps<T>(
	cell: Cell<T, unknown>,
	table: Table<T>
): HTMLAttributes<HTMLDivElement> {
	const { style, ...dataAttributes } = getStickySharedProps(cell.column);

	if (cell.column.getIsPinned() || cell.column.parent?.getIsPinned()) {
		const flatHeaders = table.getFlatHeaders();
		const header = flatHeaders.find((h) => h.column.id === cell.column.id);
		if (header) {
			const headers = findSameLevelHeaders(header, flatHeaders);

			const isLastLeftSticky = columnIsLastLeftSticky(header, headers);

			if (isLastLeftSticky) {
				// @ts-expect-error `data-*` attributes are not in the type definition
				dataAttributes['data-sticky-last-left-cell'] = true;
			}
		}
	}

	return { style, ...dataAttributes };
}
